import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Drawer,
  Flex,
  Grid,
  Layout,
  Menu,
  type MenuProps,
  Skeleton,
  Space,
  theme,
  Typography,
} from 'antd';
import type { DrawerStyles } from 'antd/es/drawer/DrawerPanel';
import { Logo } from 'pos-client/components/generic/logo/Logo.tsx';
import { useNavMenuContext } from 'pos-client/contexts/NavMenuContext.tsx';
import { useMenuItems } from 'pos-client/hooks/useMenuItems.tsx';

import './AppNavigation.css';

interface AppNavigationProps {
  isMobile?: boolean;
}

export const AppNavigation = ({ isMobile }: AppNavigationProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { Sider } = Layout;
  const { menuCollapsed, setMenuCollapsed, setMenuWidth } = useNavMenuContext();
  const screens = Grid.useBreakpoint();
  const { sideMenuItems, sideMenuItemsMobile, isSideMenuItemsLoading } =
    useMenuItems();

  const menuProps: MenuProps = useMemo(
    () => ({
      mode: 'inline',
      items: isMobile ? sideMenuItemsMobile : sideMenuItems,
      style: { border: 0 },
      multiple: false,
    }),
    [isMobile, sideMenuItemsMobile, sideMenuItems]
  );

  const drawerStyles: DrawerStyles = {
    body: {
      backgroundColor: token.Layout?.siderBg,
      padding: 0,
    },
    header: {
      padding: 0,
      paddingTop: token.paddingSM,
      paddingLeft: '26px',
      paddingRight: token.paddingXS,
      backgroundColor: token.Layout?.siderBg,
      borderBottom: 0,
    },
  };

  const menuItemsSkeleton = Array.from({ length: 10 }).map((_, index) => (
    <Flex
      key={`NavSkeleton${index}`}
      style={{
        width: '100%',
        paddingLeft: '24px',
        paddingRight: token.paddingMD,
      }}
      gap="small"
    >
      <Skeleton.Avatar size="small" active />
      <Skeleton.Button size="small" active block />
    </Flex>
  ));

  const onBreakpoint = (broken: boolean) => {
    setMenuWidth(broken ? 0 : 90);

    if (broken) {
      setMenuCollapsed(true);
    }
  };

  if (isMobile) {
    return (
      <Drawer
        open={!menuCollapsed}
        styles={drawerStyles}
        title={
          <Space size="small">
            <Logo
              style={{
                height: '18px',
                width: '20px',
                fill: 'white',
                marginRight: '3px',
              }}
            />
            <Typography.Title
              level={5}
              style={{ color: token.Menu?.itemColor }}
            >
              {t('siteName')}
            </Typography.Title>
          </Space>
        }
        maskClosable={true}
        onClose={() => setMenuCollapsed(true)}
        placement="left"
        closeIcon={<FontAwesomeIcon icon={faXmark} color="white" />}
      >
        <Menu {...menuProps} onSelect={() => setMenuCollapsed(true)} />
      </Drawer>
    );
  } else {
    return (
      <Sider
        style={{
          zIndex: 110,
          overflowY: 'auto',
          overflowX: 'hidden',
          height: '100vh',
          position: 'sticky',
          insetInlineStart: 0,
          top: 0,
          bottom: 0,
          scrollbarWidth: 'thin',
        }}
        width={'18rem'}
        trigger={null}
        collapsible
        collapsed={menuCollapsed}
        collapsedWidth="70px"
        breakpoint="xl"
        onBreakpoint={onBreakpoint}
      >
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            zIndex: 1,
            overflow: 'hidden',
          }}
        >
          <Button
            ghost={!screens.xl}
            icon={
              <FontAwesomeIcon
                icon={menuCollapsed ? faChevronRight : faChevronLeft}
                style={{ width: 12 }}
              />
            }
            onClick={() => setMenuCollapsed(!menuCollapsed)}
            style={{
              marginRight: -4,
              width: 20,
              height: '48px',
              padding: '0 3px 0 0',
              background: '#475793',
              color: 'white',
              border: 'none',
            }}
          />
        </div>
        {isSideMenuItemsLoading ? (
          <Space
            size="large"
            direction="vertical"
            style={{ height: '100%', width: '100%' }}
          >
            <Space
              size="small"
              style={{ paddingLeft: '28px', paddingTop: token.paddingSM }}
            >
              <Logo
                style={{
                  height: '18px',
                  width: '20px',
                  fill: 'white',
                  marginRight: '3px',
                }}
              />
              {!menuCollapsed && (
                <Typography.Title
                  level={5}
                  style={{ color: token.Menu?.itemColor }}
                >
                  {t('siteName')}
                </Typography.Title>
              )}
            </Space>
            {menuItemsSkeleton}
          </Space>
        ) : (
          <Menu {...menuProps} style={{ border: 0, height: '100%' }} />
        )}
      </Sider>
    );
  }
};
